import {
	createActionsHook,
	createHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type Store,
	type StoreActionApi,
} from 'react-sweet-state';

import type { AIEmojiSuggestionSource } from '../types/AIEmojiSuggestionSource.ts';

export type AIEmojiSuggestionState = {
	aiSuggestedEmojiId: string | undefined;
	aifcSuggestionInFlight: boolean;
	aiEmojiSuggestionSource: AIEmojiSuggestionSource | undefined;
	aiEmojiSuggestionSessionId: number;
	hasPasteAutoTitleEmojiAppliedThisSession: boolean;
};

type Api = StoreActionApi<AIEmojiSuggestionState>;

const initialState: AIEmojiSuggestionState = {
	aiSuggestedEmojiId: undefined,
	aifcSuggestionInFlight: false,
	aiEmojiSuggestionSource: undefined,
	aiEmojiSuggestionSessionId: 0,
	hasPasteAutoTitleEmojiAppliedThisSession: false,
};

const actions = {
	setAISuggestedEmojiId:
		(aiSuggestedEmojiId: string | undefined) =>
		({ setState }: Api): void => {
			setState({ aiSuggestedEmojiId });
		},

	setAifcSuggestionInFlight:
		(aifcSuggestionInFlight: boolean) =>
		({ setState }: Api): void => {
			setState({ aifcSuggestionInFlight });
		},

	setAIEmojiSuggestionSource:
		(aiEmojiSuggestionSource: AIEmojiSuggestionSource | undefined) =>
		({ setState }: Api): void => {
			setState({ aiEmojiSuggestionSource });
		},

	setHasPasteAutoTitleEmojiAppliedThisSession:
		(hasPasteAutoTitleEmojiAppliedThisSession: boolean) =>
		({ setState }: Api): void => {
			setState({ hasPasteAutoTitleEmojiAppliedThisSession });
		},

	/**
	 * Atomically bumps the session id and clears the companion fields. Called on
	 * each new title-generating rising edge so each session starts clean. Always
	 * prefer this over bumping `aiEmojiSuggestionSessionId` directly so the
	 * session-reset contract cannot be broken.
	 */
	resetForNewSession:
		(nextSource: AIEmojiSuggestionSource | undefined) =>
		({ getState, setState }: Api): void => {
			setState({
				aiEmojiSuggestionSessionId: getState().aiEmojiSuggestionSessionId + 1,
				aiSuggestedEmojiId: undefined,
				aifcSuggestionInFlight: false,
				hasPasteAutoTitleEmojiAppliedThisSession: false,
				aiEmojiSuggestionSource: nextSource,
			});
		},
};

export type AIEmojiSuggestionActions = typeof actions;

export const AIEmojiSuggestionStore: Store<
	AIEmojiSuggestionState,
	{
		setAISuggestedEmojiId: (aiSuggestedEmojiId: string | undefined) => ({ setState }: Api) => void;
		setAifcSuggestionInFlight: (aifcSuggestionInFlight: boolean) => ({ setState }: Api) => void;
		setAIEmojiSuggestionSource: (
			aiEmojiSuggestionSource: AIEmojiSuggestionSource | undefined,
		) => ({ setState }: Api) => void;
		setHasPasteAutoTitleEmojiAppliedThisSession: (
			hasPasteAutoTitleEmojiAppliedThisSession: boolean,
		) => ({ setState }: Api) => void;
		/**
		 * Atomically bumps the session id and clears the companion fields. Called on
		 * each new title-generating rising edge so each session starts clean. Always
		 * prefer this over bumping `aiEmojiSuggestionSessionId` directly so the
		 * session-reset contract cannot be broken.
		 */
		resetForNewSession: (
			nextSource: AIEmojiSuggestionSource | undefined,
		) => ({ getState, setState }: Api) => void;
	}
> = createStore({
	name: 'AIEmojiSuggestionStore',
	initialState,
	actions,
});

export const useAIEmojiSuggestion: HookFunction<
	AIEmojiSuggestionState,
	BoundActions<
		AIEmojiSuggestionState,
		{
			setAISuggestedEmojiId: (
				aiSuggestedEmojiId: string | undefined,
			) => ({ setState }: Api) => void;
			setAifcSuggestionInFlight: (aifcSuggestionInFlight: boolean) => ({ setState }: Api) => void;
			setAIEmojiSuggestionSource: (
				aiEmojiSuggestionSource: AIEmojiSuggestionSource | undefined,
			) => ({ setState }: Api) => void;
			setHasPasteAutoTitleEmojiAppliedThisSession: (
				hasPasteAutoTitleEmojiAppliedThisSession: boolean,
			) => ({ setState }: Api) => void;
			/**
			 * Atomically bumps the session id and clears the companion fields. Called on
			 * each new title-generating rising edge so each session starts clean. Always
			 * prefer this over bumping `aiEmojiSuggestionSessionId` directly so the
			 * session-reset contract cannot be broken.
			 */
			resetForNewSession: (
				nextSource: AIEmojiSuggestionSource | undefined,
			) => ({ getState, setState }: Api) => void;
		}
	>,
	void
> = createHook(AIEmojiSuggestionStore);
export const useAIEmojiSuggestionActions: HookActionsFunction<
	BoundActions<
		AIEmojiSuggestionState,
		{
			setAISuggestedEmojiId: (
				aiSuggestedEmojiId: string | undefined,
			) => ({ setState }: Api) => void;
			setAifcSuggestionInFlight: (aifcSuggestionInFlight: boolean) => ({ setState }: Api) => void;
			setAIEmojiSuggestionSource: (
				aiEmojiSuggestionSource: AIEmojiSuggestionSource | undefined,
			) => ({ setState }: Api) => void;
			setHasPasteAutoTitleEmojiAppliedThisSession: (
				hasPasteAutoTitleEmojiAppliedThisSession: boolean,
			) => ({ setState }: Api) => void;
			/**
			 * Atomically bumps the session id and clears the companion fields. Called on
			 * each new title-generating rising edge so each session starts clean. Always
			 * prefer this over bumping `aiEmojiSuggestionSessionId` directly so the
			 * session-reset contract cannot be broken.
			 */
			resetForNewSession: (
				nextSource: AIEmojiSuggestionSource | undefined,
			) => ({ getState, setState }: Api) => void;
		}
	>
> = createActionsHook(AIEmojiSuggestionStore);
